/**
 * Finding the zoom at which a restaurant stops being hidden inside a cluster.
 *
 * Restaurants are one Mapbox GeoJSON cluster source (`restaurants-source`,
 * `clusterRadius` 50, `clusterMaxZoom` 16). A cluster circle splits only when
 * the map zooms in, and after one split the restaurant can still sit in a
 * smaller cluster. Rather than moving the map step by step, this walks the
 * source's own cluster tree (`getClusterChildren`, `getClusterLeaves`,
 * `getClusterExpansionZoom`) to find the zoom at which the restaurant first
 * appears as its own pin, so the camera makes one combined move.
 *
 * The walk starts from the cluster that currently contains the restaurant,
 * which can only be found among clusters in the loaded tiles near the pin, so
 * a restaurant far off screen comes back `unknown` and the caller recenters
 * first, then asks again.
 */

import type { GeoJSONSource, Map as MapboxMap } from 'mapbox-gl';

/** Must match `clusterMaxZoom` on the restaurants `<Source>`. */
export const CLUSTER_MAX_ZOOM = 16;
/** Above `clusterMaxZoom` the source stops clustering, so every pin is individual here. */
export const UNCLUSTERED_ZOOM = CLUSTER_MAX_ZOOM + 1;
/** Safety bound on the tree walk (the tree is at most CLUSTER_MAX_ZOOM + 1 levels deep). */
const MAX_WALK_DEPTH = CLUSTER_MAX_ZOOM + 4;
/**
 * A containing cluster's centre is within the cluster radius (50px at the whole zoom
 * level the clusters were built for) of the pin; look this far. At a fractional zoom
 * the screen distance grows by 2 ** (zoom % 1), so the search radius scales with it.
 */
export const CLUSTER_SEARCH_RADIUS_PX = 110;
/** Most nearby clusters whose leaves are inspected. */
const MAX_CANDIDATES = 8;

export interface ClusterNode {
  geometry?: { coordinates?: number[] } | null;
  properties?: { cluster?: boolean; cluster_id?: number; point_count?: number; id?: number } | null;
}

/** Promise-shaped view of the Mapbox GeoJSON source's cluster methods. */
export interface ClusterSourceApi {
  getChildren: (clusterId: number) => Promise<ClusterNode[]>;
  getLeaves: (clusterId: number, count: number) => Promise<ClusterNode[]>;
  getExpansionZoom: (clusterId: number) => Promise<number>;
}

/**
 * Wraps a Mapbox GeoJSON source's cluster methods as promises. Each call is a
 * round trip to the source's worker, and the tree walk makes them one after
 * another (a few per restaurant at most), which is fine at this scale.
 */
export function createClusterApi(
  source: Pick<GeoJSONSource, 'getClusterChildren' | 'getClusterLeaves' | 'getClusterExpansionZoom'>
): ClusterSourceApi {
  const nodes = (resolve: (value: ClusterNode[]) => void, reject: (error: Error) => void) =>
    (error?: Error | null, features?: unknown) =>
      error ? reject(error) : resolve((features as ClusterNode[] | undefined) ?? []);
  return {
    getChildren: (id) => new Promise((resolve, reject) => void source.getClusterChildren(id, nodes(resolve, reject))),
    getLeaves: (id, count) =>
      new Promise((resolve, reject) => void source.getClusterLeaves(id, count, 0, nodes(resolve, reject))),
    getExpansionZoom: (id) =>
      new Promise((resolve, reject) =>
        void source.getClusterExpansionZoom(id, (error, zoom) =>
          error || zoom == null ? reject(error ?? new Error('no expansion zoom')) : resolve(zoom)
        )
      ),
  };
}

/** The `ClusterMapLike` view of a real Mapbox map. */
export function toClusterMap(map: MapboxMap): ClusterMapLike {
  return {
    querySourceFeatures: (sourceId, params) =>
      map.querySourceFeatures(sourceId, params as Parameters<MapboxMap['querySourceFeatures']>[1]) as ClusterNode[],
    project: (lngLat) => map.project(lngLat),
  };
}

export type UnclusterResult =
  /** The restaurant is already its own pin at the current zoom. */
  | { status: 'unclustered' }
  /** Zoom to at least this to see it as its own pin (never above UNCLUSTERED_ZOOM). */
  | { status: 'zoom'; zoom: number }
  /** Could not tell (restaurant not in the loaded tiles): recenter, then ask again. */
  | { status: 'unknown' };

const isCluster = (node: ClusterNode) => node.properties?.cluster === true && node.properties.cluster_id !== undefined;

async function clusterContains(api: ClusterSourceApi, cluster: ClusterNode, restaurantId: number): Promise<boolean> {
  const leaves = await api.getLeaves(cluster.properties!.cluster_id!, cluster.properties!.point_count ?? 1);
  return leaves.some((leaf) => leaf.properties?.id === restaurantId);
}

/**
 * Walks down from `startCluster` (known to contain the restaurant) to the zoom
 * at which the restaurant becomes its own pin. A same-coordinate pair never
 * splits, so the walk ends at UNCLUSTERED_ZOOM (the source stops clustering
 * above clusterMaxZoom); the same cap applies if the tree is not found.
 */
export async function walkToUnclusterZoom(
  api: ClusterSourceApi,
  startCluster: ClusterNode,
  restaurantId: number
): Promise<number> {
  let cluster = startCluster;
  for (let depth = 0; depth < MAX_WALK_DEPTH; depth++) {
    const clusterId = cluster.properties!.cluster_id!;
    const zoom = Math.min(await api.getExpansionZoom(clusterId), UNCLUSTERED_ZOOM);
    // Skip single-child links: the expansion zoom is where the first real split happens.
    let children = await api.getChildren(clusterId);
    while (children.length === 1 && isCluster(children[0])) {
      children = await api.getChildren(children[0].properties!.cluster_id!);
    }
    if (children.some((child) => !isCluster(child) && child.properties?.id === restaurantId)) return zoom;
    let next: ClusterNode | null = null;
    for (const child of children) {
      if (isCluster(child) && (await clusterContains(api, child, restaurantId))) {
        next = child;
        break;
      }
    }
    if (!next) return zoom;
    cluster = next;
  }
  return UNCLUSTERED_ZOOM;
}

/** The slice of a Mapbox map this needs (a fake in tests). */
export interface ClusterMapLike {
  querySourceFeatures: (sourceId: string, params?: { filter?: unknown[] }) => ClusterNode[];
  project: (lngLat: [number, number]) => { x: number; y: number };
}

/**
 * Decides whether, and how far, to zoom so `restaurantId` (at `lngLat`) shows
 * as its own pin. Never asks for a zoom below `currentZoom`.
 */
export async function resolveUnclusterZoom({
  map,
  sourceId,
  api,
  restaurantId,
  lngLat,
  currentZoom,
}: {
  map: ClusterMapLike;
  sourceId: string;
  api: ClusterSourceApi;
  restaurantId: number;
  lngLat: [number, number];
  currentZoom: number;
}): Promise<UnclusterResult> {
  const own = map.querySourceFeatures(sourceId, {
    filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'id'], restaurantId]],
  });
  if (own.length > 0) return { status: 'unclustered' };

  const pin = map.project(lngLat);
  const searchRadius = CLUSTER_SEARCH_RADIUS_PX * 2 ** (currentZoom % 1);
  const seen = new Set<number>();
  const candidates = map
    .querySourceFeatures(sourceId, { filter: ['has', 'point_count'] })
    .filter(isCluster)
    .filter((cluster) => {
      const id = cluster.properties!.cluster_id!;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .map((cluster) => {
      const [lng, lat] = cluster.geometry?.coordinates ?? [];
      const at = lng === undefined || lat === undefined ? null : map.project([lng, lat]);
      return { cluster, distance: at ? Math.hypot(at.x - pin.x, at.y - pin.y) : Infinity };
    })
    .filter((candidate) => candidate.distance <= searchRadius)
    .sort((a, b) => a.distance - b.distance)
    .slice(0, MAX_CANDIDATES);

  for (const { cluster } of candidates) {
    if (await clusterContains(api, cluster, restaurantId)) {
      const zoom = await walkToUnclusterZoom(api, cluster, restaurantId);
      return { status: 'zoom', zoom: Math.max(zoom, currentZoom) };
    }
  }
  return { status: 'unknown' };
}
